import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Check, Sparkles, ArrowRight, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = 'https://calendly.com/flytstudios/30min';

/**
 * "I Want This" quote-request modal.
 *
 * The user picks a base scope + optional add-ons. We DO NOT show prices
 * anywhere — this is a quote-request flow, not a self-serve checkout.
 * The admin (Jay) receives an email + a pre-drafted proposal in the
 * `Deals` tab with the exact selections the client made, and quotes
 * from there. This avoids anchoring clients to numbers we haven't
 * scoped yet.
 */
const IWantThisModal = ({ example, onClose }) => {
  const [selected, setSelected] = useState({});
  const [form, setForm] = useState({ name: '', email: '', brand: '', timeline: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const baseLabel = example?.base_label || 'Story sit-down package';
  const addons = example?.addons || [];

  useEffect(() => {
    const init = {};
    addons.forEach(a => { if (a.default) init[a.key] = true; });
    setSelected(init);
  }, [example?.id, addons]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  if (!example) return null;
  if (typeof document === 'undefined') return null;

  const selectedAddons = addons.filter(a => selected[a.key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Prices are intentionally sent as zero — Jay quotes from the
      // admin Deals tab. The backend detects total_price === 0 as
      // "quote requested" and adjusts the email accordingly.
      await axios.post(`${API}/flyt/deals`, {
        example_id: example.id,
        example_title: example.title,
        name: form.name,
        email: form.email,
        brand: form.brand,
        timeline: form.timeline,
        notes: form.notes,
        base_price: 0,
        addons: selectedAddons.map(a => ({ key: a.key, label: a.label, price: 0 })),
        total_price: 0,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="i-want-this-modal">
      <div className="min-h-full flex items-center justify-center p-4 lg:p-8">
        <div className="relative w-full max-w-3xl bg-[#0a0a0a] text-white rounded-3xl ring-1 ring-white/10 my-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white border border-white/20 text-white hover:text-black flex items-center justify-center transition" data-testid="i-want-this-close">
            <X className="w-5 h-5" />
          </button>

          {submitted ? (
            <div className="p-10 lg:p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-yellow-300 text-black mx-auto flex items-center justify-center mb-6">
                <Check className="w-8 h-8" strokeWidth={3} />
              </div>
              <h2 className="font-display text-3xl lg:text-4xl uppercase mb-3 leading-tight">You&apos;re in the books</h2>
              <p className="font-body text-gray-300 max-w-md mx-auto mb-2">
                We received your request. A custom quote and deck will land in <strong>{form.email}</strong> shortly.
              </p>
              <p className="font-body text-yellow-300 text-sm max-w-md mx-auto mb-8 flex items-center justify-center gap-1.5">
                <Sparkles className="w-4 h-4" /> One more step — lock in a 30-min intro so we can move fast.
              </p>

              {/* Primary: Book a call → Calendly */}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-yellow-300 hover:bg-yellow-200 text-black font-bold transition mb-3"
                data-testid="book-call-btn"
              >
                <Calendar className="w-5 h-5" />
                Book a 30-min call with Jay
                <ArrowRight className="w-4 h-4" />
              </a>

              {/* What happens next mini-card */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mt-6 mb-6 text-left">
                <p className="font-display text-xs uppercase tracking-widest text-gray-400 mb-3">What happens next</p>
                <ol className="space-y-2 text-sm text-gray-300">
                  <li className="flex gap-3"><span className="text-yellow-300 font-bold">1.</span> You pick a time on the calendar above</li>
                  <li className="flex gap-3"><span className="text-yellow-300 font-bold">2.</span> Jay scopes your project and sends a custom quote</li>
                  <li className="flex gap-3"><span className="text-yellow-300 font-bold">3.</span> 30 minutes to align on deliverables + go-date</li>
                </ol>
              </div>

              <button onClick={onClose} className="text-sm text-gray-400 hover:text-white transition" data-testid="iwt-close-btn">
                Close — I&apos;ll book later
              </button>
            </div>
          ) : (
            <div className="p-8 lg:p-10">
              <p className="font-italic text-yellow-300 text-xs uppercase tracking-widest mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Replicate this work</p>
              <h2 className="font-display text-3xl lg:text-4xl uppercase leading-tight mb-2">I want this — <span className="gold-gradient">{example.title}</span></h2>
              <p className="font-body text-gray-400 text-sm mb-7">Pick what you want. Jay will quote you a custom deck the same day.</p>

              {/* Base + addons — NO prices shown. Selections only. */}
              <div className="space-y-3 mb-7">
                <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <p className="font-bold text-sm">{baseLabel}</p>
                    <p className="text-xs text-gray-400">Included in every project</p>
                  </div>
                  <span className="font-display text-[10px] uppercase tracking-widest text-yellow-300/80">Included</span>
                </div>

                {addons.map(a => {
                  const isOn = !!selected[a.key];
                  return (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => setSelected({ ...selected, [a.key]: !isOn })}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition text-left ${isOn ? 'bg-yellow-300/10 border-yellow-300/60' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
                      data-testid={`addon-${a.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${isOn ? 'bg-yellow-300 border-yellow-300' : 'border-white/30'}`}>
                          {isOn && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                        </div>
                        <p className="font-body text-sm">{a.label}</p>
                      </div>
                      <span className={`font-display text-[10px] uppercase tracking-widest ${isOn ? 'text-yellow-300' : 'text-gray-500'}`}>
                        {isOn ? 'Add' : 'Optional'}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Lead form */}
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="i-want-this-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input type="text" required placeholder="Your name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border border-white/10 text-white placeholder-gray-500" data-testid="deal-name-input" />
                  <input type="email" required placeholder="Work email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-white/5 border border-white/10 text-white placeholder-gray-500" data-testid="deal-email-input" />
                </div>
                <input type="text" placeholder="Brand / Company" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} className="bg-white/5 border border-white/10 text-white placeholder-gray-500" data-testid="deal-brand-input" />
                <input type="text" placeholder="Ideal timeline (e.g., next month, Q1)" value={form.timeline} onChange={e => setForm({ ...form, timeline: e.target.value })} className="bg-white/5 border border-white/10 text-white placeholder-gray-500" />
                <textarea rows={3} placeholder="Anything specific we should know? (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-white/5 border border-white/10 text-white placeholder-gray-500" />

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button type="submit" disabled={submitting} className="btn-pill bg-white text-black w-full font-bold disabled:opacity-50 flex items-center justify-center gap-2" data-testid="deal-submit-btn">
                  {submitting ? 'Sending…' : <>Request a Custom Quote <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-gray-500 text-center">No charge. Jay scopes your project and sends a custom deck with pricing + next steps. You approve.</p>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default IWantThisModal;
